import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSendFollowUp } from '../../hooks/useMarketing';
import marketingService from '../../../services/api/marketingService';

const TEMPLATE_LABELS = {
  wizard_follow_up: 'First Contact',
  wizard_second_follow_up: 'Final Note',
  wizard_report_follow_up: 'Analysis Report',
  wizard_checkout_follow_up: 'Checkout Nudge',
  wizard_feedback: 'Feedback Ask',
  quote_report: 'Report & Quote',
  quote_follow_up: 'Quote Nudge',
  quote_revised: 'Updated Quote',
};

// quote_report and quote_revised are deliberately NOT offered here — sending
// the formal report or a re-quote (each with its PDF and magic link) goes
// through the Reports section, not the composer
const TEMPLATE_OPTIONS = [
  { value: 'wizard_follow_up', label: '👋 First Contact' },
  { value: 'wizard_report_follow_up', label: '📊 Analysis Report' },
  { value: 'quote_follow_up', label: '💳 Quote Nudge' },
  { value: 'wizard_checkout_follow_up', label: '🛒 Checkout Nudge (legacy)' },
  { value: 'wizard_second_follow_up', label: '⏰ Final Note' },
  { value: 'wizard_feedback', label: '💬 Feedback Ask' },
];

const BLOCK_LABELS = {
  analysis: 'Include their analysis report',
  summary: 'Include their filing summary',
  feedback: 'Include one-tap reason links',
  report: 'Include their formal report & quote',
};

/**
 * OutreachModal — compose, personalise & send outreach emails as yourself.
 *
 * The recommended template only provides the starting draft: subject and
 * body are fully editable, optional content blocks (analysis report /
 * filing summary) can be toggled, and the live preview shows exactly what
 * the recipient will receive — sent from the outreach sender's own mailbox
 * with their personal signature.
 */
function OutreachModal({ visitor, isOpen, onClose }) {
  const sendFollowUp = useSendFollowUp();
  const [selectedTemplate, setSelectedTemplate] = useState(null); // null = recommended
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [includeBlocks, setIncludeBlocks] = useState([]);
  const [activeTab, setActiveTab] = useState('compose');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const draftReady = useRef(false);

  // Load the starting draft for the (recommended or selected) template and
  // prefill the composer with it.
  const loadDraft = useCallback(async (templateOverride) => {
    if (!visitor?.id) return;
    setPreviewLoading(true);
    setPreviewError(null);
    draftReady.current = false;
    try {
      const result = await marketingService.previewEmail(visitor.id, { templateOverride });
      setPreview(result);
      setSubject(result.draft?.subject || result.subject || '');
      setBodyText(result.draft?.bodyText || '');
      setIncludeBlocks(result.draft?.defaultBlocks || []);
      draftReady.current = true;
    } catch (err) {
      console.error('Draft load failed:', err);
      setPreviewError(err.message || 'Failed to load draft');
    } finally {
      setPreviewLoading(false);
    }
  }, [visitor?.id]);

  // Re-render the preview with the composer's current content.
  const refreshPreview = useCallback(async () => {
    if (!visitor?.id || !draftReady.current) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await marketingService.previewEmail(visitor.id, {
        templateOverride: selectedTemplate || undefined,
        subject: subject || undefined,
        bodyText: bodyText || undefined,
        includeBlocks,
      });
      setPreview(result);
    } catch (err) {
      console.error('Preview failed:', err);
      setPreviewError(err.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [visitor?.id, selectedTemplate, subject, bodyText, includeBlocks]);

  // Load draft on open
  useEffect(() => {
    if (isOpen && visitor?.id) {
      setSelectedTemplate(null);
      setActiveTab('compose');
      loadDraft(undefined);
    }
  }, [isOpen, visitor?.id]);

  // Debounce preview updates while editing
  useEffect(() => {
    if (!isOpen || !visitor?.id || !draftReady.current) return;
    const timer = setTimeout(() => {
      refreshPreview();
    }, 800);
    return () => clearTimeout(timer);
  }, [subject, bodyText, includeBlocks]);

  if (!isOpen || !visitor) return null;

  const templateLabel = preview ? (TEMPLATE_LABELS[preview.templateName] || preview.templateName) : 'Loading...';
  const firstName = (visitor.name || '').split(' ')[0] || 'them';
  const sender = preview?.sender;
  const outreachCount = visitor.outreachLog?.length || 0;
  const availableBlocks = Object.keys(preview?.draft?.availableBlocks || {})
    .filter((k) => preview.draft.availableBlocks[k]);

  const handleTemplateChange = (val) => {
    const override = val === preview?.recommendedTemplate ? null : val;
    setSelectedTemplate(override);
    // Switching template replaces the draft (subject + body + blocks)
    loadDraft(override || undefined);
  };

  const toggleBlock = (block) => {
    setIncludeBlocks((prev) =>
      prev.includes(block) ? prev.filter((b) => b !== block) : [...prev, block]
    );
  };

  const handleSend = async () => {
    try {
      await sendFollowUp.mutateAsync({
        visitorId: visitor.id,
        templateOverride: selectedTemplate || undefined,
        subject: subject || undefined,
        bodyText: bodyText || undefined,
        includeBlocks,
      });
      onClose();
    } catch (err) {
      console.error('Failed to send outreach:', err);
      alert(`Failed to send: ${err.message}`);
    }
  };

  return (
    <div className="mkt-modal-overlay" onClick={onClose}>
      <div className="mkt-modal mkt-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="mkt-modal-header">
          <h2>Email {visitor.name || visitor.email} — {templateLabel}</h2>
          <button className="mkt-modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Tab bar */}
        <div className="mkt-modal-tabs">
          <button
            className={`mkt-modal-tab ${activeTab === 'compose' ? 'active' : ''}`}
            onClick={() => setActiveTab('compose')}
          >
            Compose
          </button>
          <button
            className={`mkt-modal-tab ${activeTab === 'preview' ? 'active' : ''}`}
            onClick={() => { setActiveTab('preview'); refreshPreview(); }}
          >
            Preview Email
          </button>
        </div>

        <div className="mkt-modal-body">
          {activeTab === 'compose' && (
            <>
              {/* Who they are — context for a personal opening line */}
              <div className="mkt-modal-section mkt-modal-crib">
                <h3 className="mkt-modal-section-title">About {firstName}</h3>
                <div className="mkt-modal-crib-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '4px 16px', fontSize: '13px', color: '#334155' }}>
                  {visitor.trademarkName && <div>Trademark: <strong>&ldquo;{visitor.trademarkName}&rdquo;</strong></div>}
                  {visitor.businessName && <div>Business: <strong>{visitor.businessName}</strong></div>}
                  {visitor.industry && <div>Industry: <strong>{visitor.industry}</strong></div>}
                  {visitor.currentStep && <div>Stopped at: <strong>{visitor.currentStep}</strong> {visitor.updatedAt ? `(${getTimeAgo(visitor.updatedAt)})` : ''}</div>}
                  {visitor.riskLevel && <div>Analysis risk: <strong>{visitor.riskLevel}</strong></div>}
                  {visitor.cartValue > 0 && <div>Cart value: <strong>${visitor.cartValue.toLocaleString()}</strong></div>}
                </div>
                {visitor.businessDescription && (
                  <p className="mkt-modal-hint" style={{ marginTop: '6px' }}>
                    &ldquo;{visitor.businessDescription.length > 180 ? visitor.businessDescription.substring(0, 180) + '…' : visitor.businessDescription}&rdquo;
                  </p>
                )}
              </div>

              {/* Recipient + sender + template */}
              <div className="mkt-modal-section">
                <div className="mkt-modal-field">
                  <span className="mkt-modal-label">To</span>
                  <span className="mkt-modal-value">
                    {visitor.name ? `${visitor.name} <${visitor.email}>` : visitor.email}
                  </span>
                </div>
                <div className="mkt-modal-field">
                  <span className="mkt-modal-label">From</span>
                  <span className="mkt-modal-value">
                    {sender ? `${sender.name} <${sender.email}>` : '…'}
                  </span>
                </div>
                <div className="mkt-modal-field">
                  <span className="mkt-modal-label">Template</span>
                  <span className="mkt-modal-value" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <select
                      className="mkt-template-select"
                      value={preview?.templateName || ''}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {TEMPLATE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {preview?.recommendedTemplate && preview.templateName === preview.recommendedTemplate && (
                      <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 500 }}>✓ Recommended</span>
                    )}
                    {preview?.recommendedTemplate && preview.templateName !== preview.recommendedTemplate && (
                      <span style={{ fontSize: '11px', color: '#d97706', fontWeight: 500 }}>
                        Recommended: {TEMPLATE_LABELS[preview.recommendedTemplate]}
                      </span>
                    )}
                    <button
                      type="button"
                      className="mkt-btn mkt-btn-ghost"
                      style={{ fontSize: '11px', padding: '2px 8px' }}
                      onClick={() => loadDraft(selectedTemplate || undefined)}
                      title="Discard your edits and restore the template draft"
                    >
                      ↺ Reset to template
                    </button>
                  </span>
                </div>
              </div>

              {/* Editable subject + body */}
              <div className="mkt-modal-section">
                <h3 className="mkt-modal-section-title">Your Message</h3>
                <p className="mkt-modal-hint">
                  This is your starting draft — edit anything. Adding one personal line about
                  their business makes all the difference. The continue link and your signature
                  are added automatically.
                </p>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', margin: '8px 0 4px' }}>Subject</label>
                <input
                  type="text"
                  className="mkt-modal-subject-input"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={!draftReady.current && previewLoading}
                />
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', margin: '10px 0 4px' }}>Body</label>
                <textarea
                  className="mkt-modal-textarea"
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  rows={12}
                  disabled={!draftReady.current && previewLoading}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: '14px', lineHeight: 1.5 }}
                />
                {availableBlocks.length > 0 && (
                  <div style={{ marginTop: '8px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    {availableBlocks.map((block) => (
                      <label key={block} style={{ fontSize: '13px', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={includeBlocks.includes(block)}
                          onChange={() => toggleBlock(block)}
                        />
                        {BLOCK_LABELS[block] || block}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Previous outreach */}
              {outreachCount > 0 && (
                <div className="mkt-modal-section">
                  <h3 className="mkt-modal-section-title">Previous Outreach ({outreachCount})</h3>
                  <div className="mkt-modal-outreach-log">
                    {visitor.outreachLog.map((entry, i) => (
                      <div key={entry.id || i} className="mkt-modal-outreach-entry">
                        <span className="mkt-modal-outreach-type">{TEMPLATE_LABELS[entry.template] || entry.type}</span>
                        <span className="mkt-modal-outreach-date">{new Date(entry.sentAt).toLocaleDateString()}</span>
                        <span className="mkt-modal-outreach-to">{entry.subject || entry.recipientEmail}</span>
                        {entry.sentByName && <span className="mkt-modal-outreach-by">by {entry.sentByName}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === 'preview' && (
            <div className="mkt-modal-preview-pane">
              {previewLoading && (
                <div className="mkt-modal-preview-loading">Loading preview...</div>
              )}
              {previewError && (
                <div className="mkt-modal-preview-error">
                  Failed to load preview: {previewError}
                </div>
              )}
              {preview?.html && !previewLoading && (
                <div className="mkt-modal-preview-frame">
                  <div className="mkt-modal-preview-subject">
                    <strong>Subject:</strong> {subject || preview.subject}
                  </div>
                  <iframe
                    title="Email Preview"
                    srcDoc={preview.html}
                    className="mkt-modal-preview-iframe"
                    sandbox="allow-same-origin"
                  />
                  <p className="mkt-modal-hint" style={{ marginTop: '6px' }}>
                    The button link becomes a personal &ldquo;continue where you left off&rdquo; link when sent.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mkt-modal-footer">
          <button className="mkt-btn mkt-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="mkt-btn mkt-btn-primary"
            onClick={handleSend}
            disabled={sendFollowUp.isPending || !preview || !bodyText.trim()}
          >
            {sendFollowUp.isPending ? 'Sending...' : `Send to ${firstName}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
}

export default OutreachModal;
